import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { BoardField } from '../../board-v3.types';
import { ICardSectionColumn } from '../../../../interfaces/card-section.interface';
import { BoardService } from 'src/app/services/board.service';

const INPUT_TYPE_TO_COLUMN_TYPE: Record<string, ICardSectionColumn['type']> = {
  text: 'text',
  textarea: 'textarea',
  number: 'number',
  price: 'price',
  phone: 'phone',
  email: 'email',
  link: 'link',
  date: 'date',
  select: 'select',
  checkbox: 'checkbox',
};

/** Clients block of the card detail (contacts and the company); it is not a funnel field, so it is always offered */
export const SECTION_CLIENTS_KEY = 'clients';

@Component({
  selector: 'app-board-v3-section-fields-dialog',
  templateUrl: './section-fields-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SectionFieldsDialogComponent implements OnInit, OnChanges {
  private readonly boardService = inject(BoardService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() isSaving = false;
  @Input() funnelId: string | null = null;
  @Input() activeColumns: ICardSectionColumn[] = [];

  @Output() closeChange = new EventEmitter<void>();
  @Output() apply = new EventEmitter<ICardSectionColumn[]>();

  readonly clientsKey = SECTION_CLIENTS_KEY;
  selectedKeys = new Set<string>();
  private originalColumnsByKey = new Map<string, ICardSectionColumn>();
  fields: BoardField[] = [];

  ngOnInit(): void {
    this.syncSelection();
    this.loadFields();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeColumns'] && !changes['activeColumns'].firstChange) this.syncSelection();
    if (changes['funnelId'] && !changes['funnelId'].firstChange) this.loadFields();
  }

  private syncSelection(): void {
    this.originalColumnsByKey = new Map((this.activeColumns ?? []).map((column) => [column.key, column]));
    this.selectedKeys = new Set(this.originalColumnsByKey.keys());
  }

  private loadFields(): void {
    if (!this.funnelId) return;

    this.boardService
      .getBoardFields(this.funnelId, 'FUNNEL')
      .pipe(
        catchError(() => of([] as BoardField[])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((fields) => {
        this.fields = fields;
      });
  }

  get totalCount(): number {
    return this.fields.length + 1;
  }

  get selectedCount(): number {
    const fieldsCount = this.fields.filter((field) => this.isSelected(field.identifier)).length;
    return this.isSelected(this.clientsKey) ? fieldsCount + 1 : fieldsCount;
  }

  isSelected(key: string): boolean {
    return this.selectedKeys.has(key);
  }

  setSelected(key: string, value: boolean): void {
    if (value) this.selectedKeys.add(key);
    else this.selectedKeys.delete(key);
  }

  onClose(): void {
    if (!this.isSaving) this.closeChange.emit();
  }

  onApply(): void {
    if (this.isSaving) return;

    const columns = this.fields
      .filter((field) => this.isSelected(field.identifier))
      .map((field) => this.toColumn(field));

    this.apply.emit(this.isSelected(this.clientsKey) ? [this.toClientsColumn(), ...columns] : columns);
  }

  private toClientsColumn(): ICardSectionColumn {
    return (
      this.originalColumnsByKey.get(this.clientsKey) ?? {
        key: this.clientsKey,
        title: 'board_v3_quick_create_clients',
        type: 'text',
        readonly: true,
        translate: true,
      }
    );
  }

  private toColumn(field: BoardField): ICardSectionColumn {
    const existing = this.originalColumnsByKey.get(field.identifier);
    if (existing) return existing;

    return {
      key: field.identifier,
      title: field.name,
      type: INPUT_TYPE_TO_COLUMN_TYPE[field.input_type] ?? 'text',
      options: field.options,
      readonly: field.is_readonly,
    };
  }
}
